import {isValidElement} from "react"
import {groupBy} from "lodash"

export type MarkdownValues = {
  /**
   * a record of key value pairs where keys are part of a phrase block
   * values will be interpolated into the placeholders
   *
   * @example
   * // with nested text
   * const props: MarkdownProps = {
   *   source: "hello {name}",
   *   values: {
   *     name: "Terry"
   *   }
   * }
   * //  outputs <p>hello terry</p>
   *
   * // with a nested component
   * const props: MarkdownProps = {
   *   source: "Basket: {price}",
   *   values: {
   *     price: <FormattedCurrency amount={100} />
   *   }
   * }
   * // outputs
   * (<p>Basket:
   *     <FormattedCurrency amount={100} />
   * </p>)
   */
  values?: Record<string, number | string | JSX.Element | undefined>
}

export type MarkdownComponents = Record<string, JSX.Element>

/**
 * splitValues takes the `values` object passed to Markdown and separates values
 * that are primitive values (strings and numbers) from components.
 */
export function splitValues({values = {}}: MarkdownValues) {
  const groups: any = groupBy(Object.entries(values), ([_, value]) => (isValidElement(value) ? "component" : "text"))

  // There are some type assertions here to get around the grouping confusing typescript
  const text: Record<string, number | string> = Object.fromEntries(groups.text ?? [])
  const components: MarkdownComponents = Object.fromEntries(groups.component ?? [])

  // because we have remove the component keys, phrase will log an error about missing keys
  // this puts in replacements that will leave them unchanged, i.e `foo {bar}` will just become `foo {bar}`
  // the components are then interpolated in the markdown itself
  const fallbackKeys = Object.fromEntries(Object.keys(components).map((phraseKey) => [phraseKey, `{${phraseKey}}`]))

  return {
    text: {
      ...text,
      ...fallbackKeys
    },
    components
  }
}
